import { FunctionComponent, ReactNode, SVGProps, useCallback, useRef } from "react";
import { animate, useReducedMotion } from "motion/react";
import { X } from "lucide-react";
import {
  Dialog,
  Heading,
  ModalOverlay,
  ModalOverlayProps,
  Modal as RACModal,
} from "react-aria-components";
import { cn } from "@/utils/helpers";
import { recentPointer } from "@/lib/pointerOrigin";
import { SPRING_SMOOTH } from "@/lib/motion";
import { useGrowFromCard } from "@/lib/growFromCard";
import { useCoShellPortal } from "../../CoShellPortalContext";
import { KitIconButton } from "./IconButton";

interface KitModalProps extends ModalOverlayProps {
  title?: string;
  description?: string;
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  hideCloseButton?: boolean;
  headerActions?: ReactNode;
  children: ReactNode;
  modalClassName?: string;
  overlayClassName?: string;
  "aria-label"?: string;
  /** The card this dialog was opened from: it grows out of the card and, while
   *  the card is on screen, closes back into it (card → detail). Read when the
   *  dialog opens and again as it closes. */
  growFrom?: () => Element | null;
  /** The dialog is pinned to the top of the screen, not centred: its edge does
   *  not move when it resizes, so the resize animation clips instead of sliding. */
  pinTop?: boolean;
}

/*
 * Dialog (CreativeOS Library.lib.pen, Components / Overlays, style B). A
 * dialog stops the work for one decision that cannot be undone, or an answer
 * the next step needs. Floating chrome (material tier 1), radius 16, no
 * hairline, centred on the screen. The title asks the question in Barlow
 * Condensed 800 sentence case (8 Oct), the body says exactly what happens, and the primary
 * names the verb with Cancel to its left. Close is a plain X. Pegs are retired
 * with the rounded style (1 Oct 2026).
 */

export function KitModal({
  modalClassName,
  overlayClassName,
  "aria-label": ariaLabel,
  growFrom,
  pinTop = false,
  ...props
}: KitModalProps) {
  const hasHeader = Boolean(props.title || props.icon || props.description);
  const showClose = !props.hideCloseButton;
  const showTopBar = hasHeader || showClose || Boolean(props.headerActions);
  // Portal into .co-shell so --co-* tokens resolve inside the overlay
  // (RAC otherwise portals to document.body, outside the token scope).
  const portalContainer = useCoShellPortal();
  // The dialog grows from the press that opened it (library, Foundations /
  // Motion: "travels from where it came from"), not from its own centre.
  const reduced = useReducedMotion();
  const sizeWatch = useRef<ResizeObserver | null>(null);
  const fromCard = !!growFrom;
  const aimAtCard = useGrowFromCard(growFrom, !!props.isOpen);
  const modalRef = useCallback((node: HTMLElement | null) => {
    sizeWatch.current?.disconnect();
    sizeWatch.current = null;
    if (fromCard) aimAtCard(node);
    if (!node) return;
    const press = recentPointer();
    const box = node.getBoundingClientRect();
    if (!fromCard && press && box.width && box.height) {
      const clamp = (n: number) => Math.min(150, Math.max(-50, n));
      node.style.transformOrigin = `${clamp(((press.x - box.left) / box.width) * 100)}% ${clamp(((press.y - box.top) / box.height) * 100)}%`;
    }
    /* Settings M10: when what is inside grows (a note, an error, a second
       step), the dialog grows to fit on smooth instead of jumping. It takes
       its new size at once and a translate plus a bottom clip carry it there
       (transform and clip-path only), so it opens from where its edges were. */
    if (reduced || typeof ResizeObserver === "undefined") return;
    let last = node.offsetHeight;
    const observer = new ResizeObserver(() => {
      const height = node.offsetHeight;
      const grew = height - last;
      last = height;
      if (Math.abs(grew) < 8 || node.hasAttribute("data-entering") || node.hasAttribute("data-exiting")) return;
      if (pinTop && grew < 0) return;
      const keyframes = grew > 0
        ? {
            ...(pinTop ? {} : { y: [grew / 2, 0] }),
            clipPath: [`inset(0px 0px ${grew}px 0px round 16px)`, "inset(0px 0px 0px 0px round 16px)"],
          }
        : { y: [grew / 2, 0] };
      void animate(node, keyframes, SPRING_SMOOTH).then(() => { node.style.clipPath = ""; });
    });
    observer.observe(node);
    sizeWatch.current = observer;
  }, [reduced, fromCard, aimAtCard, pinTop]);

  return (
    <ModalOverlay
      UNSTABLE_portalContainer={portalContainer ?? undefined}
      isDismissable
      {...props}
      className={({ isEntering, isExiting }) =>
        cn(
          "co-modal-overlay fixed inset-0 z-[var(--z-index-modal)] flex items-center justify-center bg-co-scrim p-4",
          /* Figma 11 · M4: the scrim fades in over 180ms ease-out; the dialog
             fades, rises 8px and grows from 96% on smooth. Closing is 160ms. */
          isEntering && "animate-in-scrim fade-in",
          isExiting && "animate-out-slow fade-out",
          overlayClassName,
        )
      }
    >
      <RACModal
        ref={modalRef}
        className={(renderProps) =>
          cn(
            "co-glass-chrome relative mx-auto max-h-[90vh] w-full min-w-[320px] max-w-[560px] overflow-y-auto rounded-panel p-6 text-co-text outline-none",
            fromCard && "co-from-card",
            !fromCard && renderProps.isEntering && "animate-in-smooth dialog-in",
            !fromCard && renderProps.isExiting && "animate-out-slow dialog-out",
            modalClassName,
          )
        }
      >
        <Dialog
          aria-label={props.title ? undefined : ariaLabel}
          // min-h-0: lets a modal that makes itself a flex column (modalClassName
          // "flex flex-col overflow-hidden") shrink this to its max height, so one
          // inner region scrolls instead of the whole box. No effect otherwise.
          className="flex h-full min-h-0 w-full flex-col outline-none"
        >
          {showTopBar ? (
            <div
              className={cn(
                "co-modal-topbar flex items-start gap-3",
                (hasHeader || props.headerActions) && "mb-4",
              )}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                {props.icon ? (
                  <span className="mb-1 grid size-9 place-items-center rounded-card bg-co-tonal">
                    <props.icon className="size-4 text-co-text" />
                  </span>
                ) : null}
                {props.title ? (
                  <Heading
                    slot="title"
                    className="font-section text-[20px] font-extrabold leading-[1.1] tracking-[0.015em] text-co-text"
                  >
                    {props.title}
                  </Heading>
                ) : null}
                {props.description ? (
                  <p className="text-[13px] leading-[1.55] text-co-muted">
                    {props.description}
                  </p>
                ) : null}
              </div>

              <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                {props.headerActions}
                {showClose ? (
                  <KitIconButton kind="close" icon={X} aria-label="Close" onClick={() => props.onOpenChange?.(false)} />
                ) : null}
              </div>
            </div>
          ) : null}

          {props.children}
        </Dialog>
      </RACModal>
    </ModalOverlay>
  );
}

/** Dialog consequence row: one line of what an action does, after a 16px icon. Most serious first. */
export function KitConsequenceRow({
  icon: Icon,
  children,
}: {
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  children: ReactNode;
}) {
  return (
    <p className="flex items-start gap-2.5 text-[14px] leading-[1.45] text-co-text2">
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/** The closing warning line of a permanent action, in alert ink. */
export function KitConsequenceWarning({ children = "This cannot be undone" }: { children?: ReactNode }) {
  return <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-co-alert-ink">{children}</p>;
}
